import { getAccessToken } from './supabase'
import { signOut } from './supabase'

const API_BASE = process.env.REACT_APP_API_BASE ||
  (window.location.hostname === 'localhost' ? '' : '/api');

const FETCH_TIMEOUT_MS = 15000;

// CSRF token cache
let _csrfToken = null;

// ETag store: url -> { etag, data }
// Persisted to sessionStorage so that page reloads can still serve cached
// payloads instantly while revalidating via If-None-Match (304).
const _ETAG_STORAGE_KEY = '_api_etag_cache_v1';
const _etagCache = (() => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(_ETAG_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
})();

let _persistTimer = null;
function _persistEtagCache() {
  if (typeof window === 'undefined') return;
  if (_persistTimer) return;
  _persistTimer = setTimeout(() => {
    _persistTimer = null;
    try {
      sessionStorage.setItem(_ETAG_STORAGE_KEY, JSON.stringify(_etagCache));
    } catch {
      // quota exceeded etc. — drop oldest half and retry once
      try {
        const keys = Object.keys(_etagCache);
        keys.slice(0, Math.floor(keys.length / 2)).forEach(k => delete _etagCache[k]);
        sessionStorage.setItem(_ETAG_STORAGE_KEY, JSON.stringify(_etagCache));
      } catch { /* give up silently */ }
    }
  }, 250);
}

// ---------------------------------------------------------------------------
// Stale-data guard: detect phone/laptop sleep and clear cached ETags so the
// next fetch gets a full 200 instead of a 304 with 2-day-old data.
// ---------------------------------------------------------------------------

let _lastActiveAt = Date.now();

/**
 * Clear all cached ETags + data.  The next apiGet for each URL will omit
 * If-None-Match and receive a full 200 response with fresh data.
 */
export function clearEtagCache() {
  Object.keys(_etagCache).forEach(k => delete _etagCache[k]);
  try { sessionStorage.removeItem(_ETAG_STORAGE_KEY); } catch { /* ok */ }
}

// Called from index.js to wire up the QueryClient invalidation callback.
let _onResumeCallback = null;
export function onResume(cb) { _onResumeCallback = cb; }

if (typeof document !== 'undefined') {
  // Track last-active time while the tab is visible.  When the tab becomes
  // visible again after being hidden for > 2 minutes (phone sleep, laptop
  // lid close, tab in background for a long time), clear ETags and trigger
  // a full React Query refetch.
  const STALE_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const elapsed = Date.now() - _lastActiveAt;
      if (elapsed > STALE_THRESHOLD_MS) {
        clearEtagCache();
        _onResumeCallback?.();
      }
    }
    _lastActiveAt = Date.now();
  });
}

// ---------------------------------------------------------------------------
// Clan slug routing — module-level state
// ---------------------------------------------------------------------------

let _clanSlug = null;

export function setClanSlug(slug) { _clanSlug = slug; }
export function getClanSlug() { return _clanSlug; }

// Paths that must NOT be clan-scoped (they are global)
const _GLOBAL_PATHS = ['/config'];

// Legacy path -> v1 clan-scoped path mapping
const _V1_PATH_MAP = {
  '/clan': '/info',
  '/clan_header': '/header',
};

function _buildClanUrl(path) {
  const basePath = path.split('?')[0];
  const query = path.includes('?') ? path.slice(path.indexOf('?')) : '';
  // Skip clan-scoping for global paths and paths already using v1
  if (!_clanSlug || _GLOBAL_PATHS.includes(basePath) || basePath.startsWith('/v1/')) {
    return `${API_BASE}${path}`;
  }
  const mapped = _V1_PATH_MAP[basePath] || basePath;
  return `${API_BASE}/v1/clan/${_clanSlug}${mapped}${query}`;
}

// ---------------------------------------------------------------------------

// Handle not_approved errors by logging out and redirecting
async function handleNotApprovedError() {
  console.warn('User not approved, logging out and redirecting')
  await signOut()
  // Clear any cached data
  Object.keys(_etagCache).forEach(key => delete _etagCache[key])
  _csrfToken = null
  // Redirect to login page
  window.location.href = '/'
}

function fetchWithTimeout(url, options = {}, timeoutMs = FETCH_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timer));
}

// Fetch CSRF token from backend
export async function fetchCsrfToken() {
  try {
    const response = await apiGet('/v1/auth/csrf-token');
    if (response && response.csrf_token) {
      _csrfToken = response.csrf_token;
      return _csrfToken;
    }
  } catch (err) {
    console.error('Failed to fetch CSRF token:', err);
  }
  return null;
}

// Get cached CSRF token or fetch new one
export async function getCsrfToken() {
  if (!_csrfToken) {
    await fetchCsrfToken();
  }
  return _csrfToken;
}

export async function apiGet(path, options = {}) {
  // No cache-buster: rely on the server's ETag + Cache-Control:max-age for revalidation.
  // Page reloads send If-None-Match so unchanged data comes back as 304 (tiny + fast).
  const url = _buildClanUrl(path);
  const cached = _etagCache[url];
  const headers = { ...(options.headers || {}) };
  if (cached && cached.etag) {
    headers['If-None-Match'] = cached.etag;
  }
  
  // Add auth token if available and requested
  if (options.withAuth) {
    const token = await getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return fetchWithTimeout(url, { headers }).then(async res => {
    if (res.status === 304 && cached) {
      return cached.data;
    }
    if (!res.ok) {
      // Handle not_approved errors by logging out
      if (res.status === 403) {
        try {
          const errorData = await res.json();
          if (errorData.error === 'not_approved') {
            await handleNotApprovedError();
            throw new Error('not_approved');
          }
        } catch (e) {
          if (e.message === 'not_approved') throw e;
        }
      }
      // Try to get error message from response body
      let errorMessage = `HTTP ${res.status}`;
      try {
        const errorData = await res.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (_) {}
      throw new Error(errorMessage);
    }
    const etag = res.headers.get('ETag');
    const lastSyncAt = res.headers.get('X-Last-Sync-At');
    const data = await res.json();
    // Auto-unwrap v1 envelope and inject fresh header values
    if (data && data.status === 'ok' && 'data' in data) {
      const unwrapped = data.data;
      if (lastSyncAt) unwrapped.last_sync_at = lastSyncAt;
      if (etag) {
        _etagCache[url] = { etag, data: unwrapped };
        _persistEtagCache();
      }
      return unwrapped;
    }
    if (lastSyncAt) data.last_sync_at = lastSyncAt;
    if (etag) {
      _etagCache[url] = { etag, data };
      _persistEtagCache();
    }
    return data;
  });
}

export async function apiPost(path, data, options = {}) {
  const _doPost = async (retryCsrf = false) => {
    const url = _buildClanUrl(path);
    const headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    };

    // Add auth token if available and requested
    if (options.withAuth) {
      const token = await getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    // Add CSRF token for state-changing operations (POST with auth)
    if (options.withAuth) {
      const csrfToken = await getCsrfToken();
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
    }

    const res = await fetchWithTimeout(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });

    // Handle CSRF errors with automatic retry
    if (res.status === 403) {
      try {
        const errorData = await res.json();
        // Handle not_approved errors by logging out
        if (errorData.error === 'not_approved') {
          await handleNotApprovedError();
          throw new Error('not_approved');
        }
        // Handle CSRF errors with automatic retry
        if (errorData.error === 'csrf_invalid' && !retryCsrf) {
          // Clear CSRF token and retry once
          _csrfToken = null;
          return _doPost(true);
        }
      } catch (e) {
        if (e.message === 'not_approved') throw e;
      }
    }

    if (!res.ok) {
      // Try to get error message from response body
      let errorMessage = `HTTP ${res.status}`;
      try {
        const errorData = await res.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (_) {}
      throw new Error(errorMessage);
    }
    const jsonData = await res.json();
    // Auto-unwrap v1 envelope
    if (jsonData && jsonData.status === 'ok' && 'data' in jsonData) {
      return jsonData.data;
    }
    return jsonData;
  };

  return _doPost(false);
}

export async function apiPut(path, data, options = {}) {
  const url = _buildClanUrl(path);
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {})
  };
  if (options.withAuth) {
    const token = await getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  if (options.withAuth) {
    const csrfToken = await getCsrfToken();
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
  }
  const res = await fetchWithTimeout(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data)
  });
  if (res.status === 403) {
    try {
      const errorData = await res.json();
      if (errorData.error === 'not_approved') {
        await handleNotApprovedError();
        throw new Error('not_approved');
      }
    } catch (e) {
      if (e.message === 'not_approved') throw e;
    }
  }
  if (!res.ok) {
    let errorMessage = `HTTP ${res.status}`;
    try {
      const errorData = await res.json();
      if (errorData.error) errorMessage = errorData.error;
      if (errorData.details) errorMessage += ': ' + errorData.details.join(', ');
    } catch (_) {}
    throw new Error(errorMessage);
  }
  const jsonData = await res.json();
  if (jsonData && jsonData.status === 'ok' && 'data' in jsonData) return jsonData.data;
  return jsonData;
}

export function sanitizeImageUrl(url) {
  if (!url || typeof url !== 'string') return '';
  if (url.startsWith('/static/')) return `${API_BASE}${url}`;
  try {
    const parsed = new URL(url);
    const allowedHosts = ['api-assets.clashofclans.com', 'coc.guide'];
    if (
      (parsed.protocol === 'https:' || parsed.protocol === 'http:') &&
      allowedHosts.some(
        h => parsed.hostname === h || parsed.hostname.endsWith('.' + h) || parsed.hostname.endsWith('.clashofclans.com')
      )
    ) {
      return url;
    }
  } catch (_) {}
  return '';
}

export function escapeHtml(text) {
  if (text === null || text === undefined) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}
